<template>
  <div ref="rootRef" class="relative">
    <button
      ref="menuTrigger"
      type="button"
      class="relative z-10 flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-sm font-medium transition-colors lg:px-3.5"
      :class="
        modelValue || hasActiveItem
          ? 'bg-white text-brand-600 dark:bg-zinc-800 dark:text-brand-400'
          : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
      "
      :aria-expanded="modelValue ? 'true' : 'false'"
      aria-haspopup="true"
      :aria-label="__('Menu')"
      @click="toggle"
    >
      <NavIcon
        name="squares-2x2"
        class="size-4 shrink-0"
        :class="
          modelValue || hasActiveItem
            ? 'text-brand-600 dark:text-brand-400'
            : 'text-current'
        "
        width="16"
        height="16"
      />
      <span class="hidden lg:inline">{{ __('Menu') }}</span>
      <Icon
        type="chevron-down"
        class="size-3.5 shrink-0 transition-transform duration-200"
        :class="modelValue ? 'rotate-180' : ''"
      />
    </button>

    <teleport to="body">
      <div
        v-if="modelValue"
        class="panel-mega-menu-backdrop fixed inset-0 z-[99]"
        aria-hidden="true"
        @click="close"
      />

      <transition
        enter-active-class="panel-mega-menu-dropdown-enter-active"
        enter-from-class="panel-mega-menu-dropdown-enter-from"
        enter-to-class="panel-mega-menu-dropdown-enter-to"
        leave-active-class="panel-mega-menu-dropdown-leave-active"
        leave-from-class="panel-mega-menu-dropdown-leave-from"
        leave-to-class="panel-mega-menu-dropdown-leave-to"
      >
        <div
          v-if="modelValue"
          ref="menuPanel"
          class="panel-mega-menu-dropdown-panel pointer-events-auto fixed z-[100]"
          :style="panelStyle"
        >
          <div class="panel-glass panel-mega-menu overflow-hidden">
            <div class="panel-glass-content panel-mega-menu__header">
              <p class="panel-mega-menu__title">
                {{ __('Menu') }}
              </p>
              <p class="panel-mega-menu__subtitle">
                {{ __('More panel sections') }}
              </p>
            </div>

            <div class="panel-glass-content panel-mega-menu__grid">
              <template v-for="item in items" :key="item.key">
                <div
                  v-if="item.type === 'menu'"
                  class="panel-mega-menu__section"
                  :class="{ 'panel-mega-menu__section--active': item.active }"
                >
                  <div class="panel-mega-menu__section-head">
                    <span
                      v-if="item.icon"
                      class="panel-mega-menu__section-icon"
                      aria-hidden="true"
                    >
                      <NavIcon :name="item.icon" width="15" height="15" />
                    </span>

                    <span class="panel-mega-menu__section-name">{{ item.name }}</span>
                  </div>

                  <div class="panel-mega-menu__links">
                    <Link
                      v-for="child in item.items"
                      :key="child.key"
                      :href="child.path"
                      class="panel-mega-menu__link"
                      :class="{ 'panel-mega-menu__link--active': child.active }"
                      :aria-current="child.active ? 'page' : undefined"
                      @click="close"
                    >
                      <span class="min-w-0 truncate">{{ child.name }}</span>
                    </Link>
                  </div>
                </div>

                <Link
                  v-else
                  :href="item.path"
                  class="panel-mega-menu__standalone"
                  :class="{ 'panel-mega-menu__standalone--active': item.active }"
                  :aria-current="item.active ? 'page' : undefined"
                  @click="close"
                >
                  <span
                    v-if="item.icon"
                    class="panel-mega-menu__standalone-icon"
                    aria-hidden="true"
                  >
                    <NavIcon :name="item.icon" width="15" height="15" />
                  </span>
                  <span class="min-w-0 truncate">{{ item.name }}</span>
                </Link>
              </template>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useLocalization } from '../../composables/useLocalization'
import NavIcon from './NavIcon.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue'])

const { __ } = useLocalization()

const rootRef = ref(null)
const menuTrigger = ref(null)
const menuPanel = ref(null)

const { floatingStyles } = useFloating(menuTrigger, menuPanel, {
  strategy: 'fixed',
  placement: 'bottom',
  transform: false,
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(12),
    flip(),
    shift({ padding: 16 }),
    size({
      /**
       * Constrain panel width while keeping it within the viewport
       *
       * @param {{ availableWidth: number, elements: { floating: HTMLElement } }} state
       */
      apply({ availableWidth, elements }) {
        Object.assign(elements.floating.style, {
          width: `${Math.min(520, availableWidth - 32)}px`,
        })
      },
    }),
  ],
})

/**
 * Merged floating-ui styles for the teleported panel
 */
const panelStyle = computed(() => ({
  zIndex: 100,
  ...floatingStyles.value,
}))

/**
 * Whether any overflow nav item or nested child is currently active
 */
const hasActiveItem = computed(() =>
  props.items.some(item => {
    if (item.type === 'menu') {
      return Boolean(item.active || item.items?.some(child => child.active))
    }

    return Boolean(item.active)
  })
)

let stopClickOutside

/**
 * Close the mega menu panel
 */
function close() {
  emit('update:modelValue', false)
}

/**
 * Toggle the mega menu panel
 *
 * @param {Event} event
 */
function toggle(event) {
  event.stopPropagation()
  emit('update:modelValue', !props.modelValue)
}

/**
 * Bind click-outside handling for the mega menu panel
 */
function bindClickOutside() {
  stopClickOutside?.()
  stopClickOutside = onClickOutside(
    rootRef,
    () => close(),
    { ignore: [menuPanel] }
  )
}

/**
 * Close the mega menu when Escape is pressed
 *
 * @param {KeyboardEvent} event
 */
function handleEscape(event) {
  if (event.key === 'Escape') {
    close()
  }
}

watch(
  () => props.modelValue,
  open => {
    if (open) {
      nextTick(() => bindClickOutside())
      document.addEventListener('keydown', handleEscape)
      return
    }

    stopClickOutside?.()
    stopClickOutside = undefined
    document.removeEventListener('keydown', handleEscape)
  }
)

onBeforeUnmount(() => {
  stopClickOutside?.()
  document.removeEventListener('keydown', handleEscape)
})
</script>
